import { useQuery, useQueryClient, type QueryStatus } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { DBProps, IRegistrationConsent, OmitDBProps, dbProps } from 'types'
import { FetchStatus, getDocs, usePrefCurrentWorkspace } from 'utils'

export const makeEmptyRegistrationConsent = (workspaceId: string): OmitDBProps<IRegistrationConsent> => ({
  workspaceId,
  title: '',
})

export type RegistrationConsentRecord = Pick<IRegistrationConsent, keyof DBProps | (typeof partialRegistrationConsentFields)[number]>

const partialRegistrationConsentFields = ['title', 'workspaceId'] as const satisfies (keyof IRegistrationConsent)[]

const makeQueryKey = (workspaceId?: string) => ['registrationConsents', workspaceId] as const

export function useRegistrationConsents() {
  const [workspaceId] = usePrefCurrentWorkspace()
  const enabled = !!workspaceId
  const query = useQuery({
    queryKey: makeQueryKey(workspaceId),
    queryFn: () => fetchRegistrationConsents(workspaceId!),
    enabled,
    staleTime: 1000 * 30,
  })

  const registrationConsents = query.data ?? []
  const status = getFetchStatus(query.status, registrationConsents)

  return { registrationConsents, status }
}

export function useMutateRegistrationConsentRecords() {
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()

  async function updateRegistrationConsentRecord(body: IRegistrationConsent) {
    if (!workspace) return

    const record = makeRegistrationConsentRecord(body)

    queryClient.setQueriesData<RegistrationConsentRecord[]>(
      { queryKey: makeQueryKey(workspace.id) },
      registrationConsents => {
        if (!registrationConsents) return [record]

        const consentIndex = registrationConsents.findIndex(e => e.id === record.id)
        if (consentIndex < 0) return sortRegistrationConsentRecords([...registrationConsents, record])

        return sortRegistrationConsentRecords(registrationConsents.map((e, i) => (i === consentIndex ? record : e)))
      }
    )
  }

  async function removeRegistrationConsentRecord(id: string) {
    if (!user || !workspace) return

    queryClient.setQueriesData<RegistrationConsentRecord[]>(
      { queryKey: makeQueryKey(workspace.id) },
      registrationConsents => {
        if (!registrationConsents) return []

        const consentIndex = registrationConsents.findIndex(e => e.id === id)
        if (consentIndex < 0) return registrationConsents

        return registrationConsents.filter((_, i) => i !== consentIndex)
      }
    )
  }

  return { updateRegistrationConsentRecord, removeRegistrationConsentRecord }
}

async function fetchRegistrationConsents(workspaceId: string) {
  const registrationConsents = await getDocs({
    collection: 'registrationConsents',
    fields: partialRegistrationConsentFields,
    sort: { by: 'title', order: 'ASC' },
    constraints: [{ field: 'workspaceId', op: '==', value: workspaceId }],
  })

  return sortRegistrationConsentRecords(registrationConsents.data.map(doc => makeRegistrationConsentRecord(doc as any)))
}

function makeRegistrationConsentRecord(registrationConsent: RegistrationConsentRecord) {
  return pick(registrationConsent, [...dbProps, ...partialRegistrationConsentFields]) as RegistrationConsentRecord
}

function sortRegistrationConsentRecords(registrationConsents: RegistrationConsentRecord[]) {
  return [...registrationConsents].sort((a, b) => a.title.localeCompare(b.title))
}

function getFetchStatus(status: QueryStatus, registrationConsents: RegistrationConsentRecord[]): FetchStatus {
  if (status === 'pending') return 'loading'
  if (status === 'error') return 'error'
  return registrationConsents.length ? 'success' : 'empty'
}